import { KeyboardEvent, RefObject, useCallback, useRef, useState } from "react";

export interface SimpleSearchBarProps {
    onActiveChange?: ((active: boolean) => void) | undefined;
    onSearchChange?: ((value: string) => void) | undefined;
    placeholder?: string | undefined;
}

export interface UseSimpleSearchBarResult {
    value: string;
    focused: boolean;
    // NOTE: allow null here to match useRef<HTMLInputElement | null>(null)
    inputRef: RefObject<HTMLInputElement | null>;
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
    handleBlur: () => void;
    handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    handleClearMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClearClick: () => void;
}

export const useSimpleSearchBar = ({
    onActiveChange,
    onSearchChange,
}: SimpleSearchBarProps): UseSimpleSearchBarResult => {
    const [value, setValue] = useState("");
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    // Keep value in a ref so blur callback reads the latest without re-creating
    const valueRef = useRef(value);
    valueRef.current = value;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const v = event.target.value;
            setValue(v);
            onSearchChange?.(v);
            // If user types something while sheet is closed, open it
            if (v.trim()) {
                onActiveChange?.(true);
            }
        },
        [onSearchChange, onActiveChange],
    );

    const handleFocus = useCallback(() => {
        setFocused(true);
        onActiveChange?.(true);
    }, [onActiveChange]);

    const handleBlur = useCallback(() => {
        setFocused(false);
        // Keep the search sheet open if there's text — only X closes it
        if (!valueRef.current.trim()) {
            onActiveChange?.(false);
        }
    }, [onActiveChange]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Escape") {
                // Escape clears text and closes
                setValue("");
                onSearchChange?.("");
                onActiveChange?.(false);
                inputRef.current?.blur();
            }
        },
        [onActiveChange, onSearchChange],
    );

    const handleClearMouseDown = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            // prevent losing focus before click fires
            event.preventDefault();
        },
        [],
    );

    const handleClearClick = useCallback(() => {
        setValue("");
        onSearchChange?.("");
        onActiveChange?.(false); // close search sheet
        inputRef.current?.blur(); // defocus input
    }, [onActiveChange, onSearchChange]);

    return {
        value,
        focused,
        inputRef,
        handleChange,
        handleFocus,
        handleBlur,
        handleKeyDown,
        handleClearMouseDown,
        handleClearClick,
    };
};
